import React, { useState } from 'react';
import {
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

interface LoginProps {
  onLoginSuccess: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(username, password);
      onLoginSuccess();
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Identifiants incorrects');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div className="text-center">
          <img
            className="mx-auto h-16 w-auto object-contain drop-shadow-md"
            src="/logo512.png"
            alt="Logo Isiforecast"
          />
          <h1 className="mt-4 text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
            Isiforecast
          </h1>
          <p className="mt-2 text-lg text-gray-600 font-light tracking-wide">
            Analyses et prédictions
          </p>
          <div className="mt-8 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
          <h2 className="mt-8 text-xl font-medium text-gray-700">
            Connectez-vous à votre compte
          </h2>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4 mt-4 border border-red-100">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Nom d'utilisateur"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            autoComplete="username"
            size="small"
            sx={{ borderRadius: 2 }}
          />

          <TextField
            fullWidth
            type="password"
            label="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            autoComplete="current-password"
            size="small"
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              mt: 3,
              height: 42,
              textTransform: 'none',
              borderRadius: 2,
              background: 'linear-gradient(to right, #3b82f6, #6366f1)',
              '&:hover': {
                background: 'linear-gradient(to right, #2563eb, #4f46e5)',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Se connecter'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
